import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Trash2, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';

const AdminScorecardFaqsPage = () => {
    const [faqs, setFaqs] = useState([]);
    const [settings, setSettings] = useState({
        tag_text: 'Got Questions?',
        title_text: 'Frequently Asked Questions',
        subtitle_text: 'Find quick answers to common queries about our eligibility calculator and home loan process.'
    });
    const [loading, setLoading] = useState(true);
    const [editingFaq, setEditingFaq] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, faqId: null });

    const [formData, setFormData] = useState({
        category: 'About the Calculator',
        question: '',
        answer: '',
        display_order: 0,
        is_active: true
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [faqsRes, settingsRes] = await Promise.all([
                axios.get('/api/admin/eligibility-scorecard/faqs'),
                axios.get('/api/admin/eligibility-scorecard/faqs/settings')
            ]);

            if (faqsRes.data.success) {
                setFaqs(faqsRes.data.data);
            }
            if (settingsRes.data.success && settingsRes.data.data) {
                setSettings({
                    tag_text: settingsRes.data.data.tag_text || '',
                    title_text: settingsRes.data.data.title_text || '',
                    subtitle_text: settingsRes.data.data.subtitle_text || ''
                });
            }
        } catch (error) {
            console.error('Error fetching FAQ data:', error);
            toast.error('Failed to load FAQ section data');
        } finally {
            setLoading(false);
        }
    };

    const handleSettingsSave = async (e) => {
        e.preventDefault();
        try {
            setSavingSettings(true);
            const response = await axios.put('/api/admin/eligibility-scorecard/faqs/settings', settings);
            if (response.data.success) {
                toast.success('Settings updated successfully');
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleEdit = (faq) => {
        setEditingFaq(faq);
        setIsAdding(false);
        setFormData({
            category: faq.category,
            question: faq.question,
            answer: faq.answer,
            display_order: faq.display_order,
            is_active: faq.is_active === 1 || faq.is_active === true
        });
    };

    const handleCancel = () => {
        setEditingFaq(null);
        setIsAdding(false);
        setFormData({
            category: 'About the Calculator',
            question: '',
            answer: '',
            display_order: 0,
            is_active: true
        });
    };

    const handleSaveFaq = async () => {
        try {
            if (!formData.question || !formData.answer || !formData.category) {
                toast.error('Please fill in all required fields');
                return;
            }

            if (isAdding) {
                const response = await axios.post('/api/admin/eligibility-scorecard/faqs', formData);
                if (response.data.success) {
                    toast.success('FAQ added successfully');
                    fetchData();
                    handleCancel();
                }
            } else {
                const response = await axios.put(`/api/admin/eligibility-scorecard/faqs/${editingFaq.id}`, formData);
                if (response.data.success) {
                    toast.success('FAQ updated successfully');
                    fetchData();
                    handleCancel();
                }
            }
        } catch (error) {
            console.error('Error saving FAQ:', error);
            toast.error('Failed to save FAQ');
        }
    };

    const confirmDelete = async () => {
        try {
            const response = await axios.delete(`/api/admin/eligibility-scorecard/faqs/${deleteConfirm.faqId}`);
            if (response.data.success) {
                toast.success('FAQ deleted successfully');
                fetchData();
                setDeleteConfirm({ isOpen: false, faqId: null });
            }
        } catch (error) {
            console.error('Error deleting FAQ:', error);
            toast.error('Failed to delete FAQ');
        }
    };

    // Get unique categories list for helper selections
    const uniqueCategories = [...new Set(faqs.map(item => item.category))];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">FAQ Section</h1>
                    <p className="text-slate-500 mt-1">Manage the "Got Questions?" (Frequently Asked Questions) section on the Scorecard page.</p>
                </div>
                {!isAdding && !editingFaq && (
                    <button
                        onClick={() => setIsAdding(true)}
                        className="px-4 py-2 bg-[#283079] text-white rounded-lg hover:bg-[#1f2559] flex items-center gap-2 font-semibold shadow-md"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Add New FAQ
                    </button>
                )}
            </div>

            {/* Section Settings */}
            {!isAdding && !editingFaq && (
                <form onSubmit={handleSettingsSave} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
                    <h2 className="text-lg font-bold text-slate-800 mb-2">Section Header Settings</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Section Tag Text
                                </label>
                                <input
                                    type="text"
                                    value={settings.tag_text}
                                    onChange={(e) => setSettings({ ...settings, tag_text: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Section Title
                                </label>
                                <input
                                    type="text"
                                    value={settings.title_text}
                                    onChange={(e) => setSettings({ ...settings, title_text: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Subtitle Text
                            </label>
                            <textarea
                                value={settings.subtitle_text}
                                onChange={(e) => setSettings({ ...settings, subtitle_text: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#283079] focus:border-transparent"
                                rows="4"
                                required
                            />
                        </div>
                    </div>
                    <div className="flex justify-end pt-2">
                        <button
                            type="submit"
                            disabled={savingSettings}
                            className="bg-[#283079] hover:bg-[#1f2559] text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                        >
                            {savingSettings ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>
            )}

            {/* FAQ Form */}
            {(isAdding || editingFaq) && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                    <h3 className="text-xl font-bold mb-6 text-slate-800 border-b pb-4">{isAdding ? 'Add New FAQ' : 'Edit FAQ'}</h3>
                    <div className="space-y-4 mb-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Category <span className="text-red-500">*</span></label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                    placeholder="Enter or select category"
                                />
                                {uniqueCategories.length > 0 && (
                                    <select
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                setFormData({ ...formData, category: e.target.value });
                                            }
                                        }}
                                        className="px-4 py-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-700"
                                    >
                                        <option value="">-- Select Existing Category --</option>
                                        {uniqueCategories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Question <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={formData.question}
                                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                placeholder="e.g. Is the calculator free?"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Answer <span className="text-red-500">*</span></label>
                            <textarea
                                value={formData.answer}
                                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                rows="4"
                                placeholder="e.g. Yes, and no documents are needed to use it."
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                                <input
                                    type="number"
                                    value={formData.display_order}
                                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#283079]"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mb-6 flex items-center gap-3 bg-slate-50 p-4 rounded-xl">
                        <div className="flex items-center h-5">
                            <input
                                id="is_active"
                                type="checkbox"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="w-5 h-5 text-[#283079] border-slate-300 rounded focus:ring-[#283079]"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label htmlFor="is_active" className="text-sm font-semibold text-slate-800">
                                Active Status
                            </label>
                            <p className="text-xs text-slate-500">
                                When active, this FAQ will be visible on the public scorecard page.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-3 mt-8 justify-end">
                        <button
                            onClick={handleCancel}
                            className="px-6 py-2.5 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200 flex items-center gap-2"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveFaq}
                            className="px-6 py-2.5 bg-[#283079] text-white font-semibold rounded-xl hover:bg-[#1f2559] flex items-center gap-2 shadow-md"
                        >
                            {isAdding ? 'Save FAQ' : 'Update FAQ'}
                        </button>
                    </div>
                </div>
            )}

            {/* FAQs List Table */}
            {!isAdding && !editingFaq && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Order</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Status</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Category</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Question</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Answer</th>
                                    <th className="p-4 font-semibold text-slate-700 text-sm">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-slate-500">Loading...</td>
                                    </tr>
                                ) : faqs.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-slate-500">No FAQs found. Add one above.</td>
                                    </tr>
                                ) : (
                                    faqs.map((item) => (
                                        <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                            <td className="p-4 text-slate-500 text-sm font-medium">#{item.display_order}</td>
                                            <td className="p-4">
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${item.is_active ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                                                    {item.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="p-4 text-slate-800 text-sm font-bold">{item.category}</td>
                                            <td className="p-4 text-slate-800 text-sm font-medium">{item.question}</td>
                                            <td className="p-4 text-slate-600 text-sm truncate max-w-[200px]">{item.answer}</td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleEdit(item)}
                                                        className="p-1.5 hover:bg-slate-200 rounded text-slate-500 hover:text-[#283079] transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirm({ isOpen: true, faqId: item.id })}
                                                        className="p-1.5 hover:bg-red-100 rounded text-slate-500 hover:text-red-600 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, faqId: null })}
                onConfirm={confirmDelete}
                title="Delete FAQ"
                message="Are you sure you want to delete this FAQ? This action cannot be undone."
            />
        </div>
    );
};

export default AdminScorecardFaqsPage;
